import { useEffect, useState } from "react";
import { useBranding } from "../branding";
import { MarkdownView } from "./Markdown";
import { splitProcesso } from "../utils/processo";

interface SharedTurn {
  role: "user" | "assistant";
  content: string;
  ts: string;
  aborted?: boolean;
}

interface SharedData {
  title: string;
  sharedAt: string | null;
  turns: SharedTurn[];
}

/** Visualização pública (sem login) de uma conversa compartilhada via link. */
export function SharedConversationPage({ shareId }: { shareId: string }) {
  const { appName, logoDataUrl } = useBranding();
  const [data, setData] = useState<SharedData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(`/api/share/${encodeURIComponent(shareId)}`);
        if (!r.ok) {
          const j = await r.json().catch(() => ({}) as { error?: string });
          throw new Error(j.error || "Link não encontrado ou revogado.");
        }
        const j = (await r.json()) as SharedData;
        if (!cancelled) setData(j);
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : "Falha ao carregar a conversa.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [shareId]);

  return (
    <div className="shared-page">
      <header className="shared-page-header">
        {logoDataUrl ? <img src={logoDataUrl} alt={appName} className="brand-logo" /> : <span className="brand-dot" />}
        <span>{appName}</span>
      </header>
      <div className="shared-page-body">
        {loading ? (
          <p className="shared-page-status">Carregando conversa…</p>
        ) : error ? (
          <p className="shared-page-status shared-page-error">{error}</p>
        ) : data ? (
          <>
            <h1 className="shared-page-title">{data.title}</h1>
            {data.sharedAt && (
              <p className="shared-page-meta">
                Compartilhada em {new Date(data.sharedAt).toLocaleString("pt-BR", { timeZone: "America/Sao_Paulo" })}
              </p>
            )}
            <div className="shared-page-turns">
              {data.turns.map((t, i) => (
                <div key={i} className={`shared-turn shared-turn-${t.role}`}>
                  <span className="shared-turn-role">{t.role === "user" ? "Pergunta" : appName}</span>
                  {t.role === "user" ? (
                    <div className="shared-turn-content user-content">{t.content}</div>
                  ) : (
                    <div className="shared-turn-content markdown">
                      <MarkdownView content={splitProcesso(t.content).answer} />
                      {t.aborted ? <div className="aborted-note">Resposta interrompida</div> : null}
                    </div>
                  )}
                </div>
              ))}
            </div>
            <p className="shared-page-footer">Link de leitura pública - visualização somente, sem necessidade de login.</p>
          </>
        ) : null}
      </div>
    </div>
  );
}
